/**
 * ArchiveSkusDialog: the Archive / Unarchive flow on /products and
 * /products/:id, over the BLU-1276 backend (`inventory.getSkuUsage`,
 * `inventory.retireSkus`, `inventory.unretireSkus`; Admin + Super Admin).
 *
 * Archive shows the usage guard's findings BEFORE the operator confirms:
 * which SKUs stay live and why, and any warnings, all localized. Only the
 * SKUs the guard clears are sent, and the server re-checks inside the same
 * statement that flips the flag (a SKU that came into use since the check is
 * refused, never archived). Nothing here writes to Shopify.
 *
 * "Still live on Shopify" follows the SAME fresh `shopifyStatus` the row
 * badge shows (`resolveShopifyLiveness` in ../utils/sku-status), never the
 * guard's own warning, which reads the stale products.status.
 *
 * The mode and the target list are SNAPSHOT when the dialog opens, and the
 * usage check starts from that same place. The caller closes the dialog by
 * resetting its own state, so the `mode` and `targets` props snap back to
 * their defaults during the exit animation, and the post-write invalidate
 * prunes the caller's selection. Neither may rewrite the copy or the list
 * the operator is reading, and a reopen never checks the previous selection.
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SKU_RETIREMENT_MAX_IDS, SKU_USAGE_QUERY_MAX_IDS } from '@threadheads/shared';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SectionError } from '@/components/shared/SectionError';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { resolveShopifyLiveness, type ArchiveSkuTarget } from '../utils/sku-status';

type SkuUsageRow = RouterOutputs['inventory']['getSkuUsage'][number];
type Mode = 'archive' | 'unarchive';
type Session = { mode: Mode; targets: ArchiveSkuTarget[] };
type Outcome = { skuId: string; sku: string | null; result: 'done' | 'refused' | 'not_found' };

const REASON_MAX = 500;
/** Usage reads in flight at once: 1,000 SKUs is 25 reads, never all at once. */
const USAGE_CONCURRENCY = 3;
/** Refused SKU codes named in the toast before "and N more". */
const REFUSED_NAMED_MAX = 5;
/** The per-SKU "still live on Shopify" line reuses the guard's warning copy. */
const SHOPIFY_LIVE_LINE = 'shopify_product_active';

const chunk = <T,>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Mode;
  targets: ArchiveSkuTarget[];
  /** The pass finished. `refusedIds` came into use since the check and were
   *  not archived, so the caller can keep them selected. */
  onDone?: (refusedIds: string[]) => void;
};

type Reviewed = {
  target: ArchiveSkuTarget;
  blocking: boolean;
  reasons: string[];
  warnings: string[];
  shopifyLive: boolean;
};

function SkuCodeList({
  heading,
  items,
}: {
  heading: string;
  items: Array<{ id: string; sku: string; lines: string[] }>;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{heading}</p>
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.id} className="text-sm">
            <span className="font-mono">{item.sku}</span>
            <span className="block text-xs text-muted-foreground">{item.lines.join(', ')}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ArchiveSkusDialog({ open, onOpenChange, mode, targets, onDone }: Props) {
  const { t } = useTranslation('inventory');
  const utils = trpc.useUtils();
  const retireMutation = trpc.inventory.retireSkus.useMutation();
  const unretireMutation = trpc.inventory.unretireSkus.useMutation();

  const [session, setSession] = useState<Session>({ mode, targets: [] });
  const [usage, setUsage] = useState<Map<string, SkuUsageRow> | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const modeRef = useRef(mode);
  modeRef.current = mode;
  const targetsRef = useRef(targets);
  targetsRef.current = targets;
  const requestRef = useRef(0);

  const loadUsage = useCallback(
    async (ids: string[]) => {
      const requestId = ++requestRef.current;
      setUsage(null);
      setLoadError(false);
      setLoading(true);
      try {
        const rows: SkuUsageRow[] = [];
        const chunks = chunk(ids, SKU_USAGE_QUERY_MAX_IDS);
        for (let i = 0; i < chunks.length; i += USAGE_CONCURRENCY) {
          const pages = await Promise.all(
            chunks
              .slice(i, i + USAGE_CONCURRENCY)
              .map((skuIds) => utils.inventory.getSkuUsage.fetch({ skuIds })),
          );
          if (requestId !== requestRef.current) return;
          for (const page of pages) rows.push(...page);
        }
        setUsage(new Map(rows.map((row) => [row.skuId, row])));
      } catch {
        if (requestId !== requestRef.current) return;
        setLoadError(true);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [utils],
  );

  // Snapshot, reset and start the usage check BEFORE paint, all in one place
  // (see BulkDeleteProductsDialog for why a layout effect, not an effect).
  useLayoutEffect(() => {
    if (!open) {
      // Drop any late usage response from the session that just closed.
      requestRef.current += 1;
      return;
    }
    const next: Session = { mode: modeRef.current, targets: targetsRef.current };
    setSession(next);
    setUsage(null);
    setLoadError(false);
    setLoading(false);
    setReason('');
    setSubmitting(false);
    if (next.mode === 'archive' && next.targets.length > 0) {
      void loadUsage(next.targets.map((target) => target.id));
    }
  }, [open, loadUsage]);

  const archive = session.mode === 'archive';

  const reviewed: Reviewed[] = useMemo(() => {
    if (!usage) return [];
    return session.targets.map((target) => {
      const row = usage.get(target.id);
      if (!row) {
        // No usage row came back: it could not be checked, so it is not
        // cleared and never sent.
        return { target, blocking: true, reasons: ['not_checked'], warnings: [], shopifyLive: false };
      }
      const { shopifyLive, warnings } = resolveShopifyLiveness(target, row.warnings);
      return { target, blocking: row.blocking, reasons: row.reasons, warnings, shopifyLive };
    });
  }, [session.targets, usage]);

  // Unknown server codes render as the code with spaces, never raw English
  // from the server and never blank.
  const reasonText = (code: string) =>
    t(`products.archive.reason.${code}`, { defaultValue: code.replace(/_/g, ' ') });
  const warningText = (code: string) =>
    t(`products.archive.warning.${code}`, { defaultValue: code.replace(/_/g, ' ') });

  const clear = reviewed.filter((r) => !r.blocking);
  const blocked = reviewed.filter((r) => r.blocking);
  // Only SKUs that will actually be archived can be "still live on Shopify";
  // a blocked SKU stays live anyway.
  const liveOnShopify = clear.filter((r) => r.shopifyLive).length;
  const warned = clear
    .map((r) => ({
      id: r.target.id,
      sku: r.target.sku,
      lines: [...(r.shopifyLive ? [SHOPIFY_LIVE_LINE] : []), ...r.warnings].map(warningText),
    }))
    .filter((item) => item.lines.length > 0);

  const trimmedReason = reason.trim();
  const archiveDisabled =
    loading || submitting || usage === null || clear.length === 0 || trimmedReason.length === 0;
  // Before the check lands (or when it failed) the count is the selection,
  // never "Archive 0 SKUs".
  const confirmCount = archive && usage !== null ? clear.length : session.targets.length;

  /** One chunk, normalized: SKUs now in the target state, refused, not found. */
  const sendChunk = async (skuIds: string[]): Promise<Outcome[]> => {
    if (archive) {
      const { results } = await retireMutation.mutateAsync({ skuIds, reason: trimmedReason });
      return results.map((r) => ({
        skuId: r.skuId,
        sku: r.sku,
        // already_retired counts as done: a retry after a gateway timeout
        // must not report "Archived 0".
        result:
          r.outcome === 'retired' || r.outcome === 'already_retired'
            ? 'done'
            : r.outcome === 'refused'
              ? 'refused'
              : 'not_found',
      }));
    }
    const { results } = await unretireMutation.mutateAsync({ skuIds });
    return results.map((r) => ({
      skuId: r.skuId,
      sku: r.sku,
      result: r.outcome === 'not_found' ? 'not_found' : 'done',
    }));
  };

  const runPass = async () => {
    const ids = archive ? clear.map((r) => r.target.id) : session.targets.map((target) => target.id);
    if (ids.length === 0 || (archive && trimmedReason.length === 0)) return;
    setSubmitting(true);

    const outcomes: Outcome[] = [];
    let failed = false;
    try {
      for (const skuIds of chunk(ids, SKU_RETIREMENT_MAX_IDS)) outcomes.push(...(await sendChunk(skuIds)));
    } catch {
      failed = true;
    }

    const done = outcomes.filter((o) => o.result === 'done');
    const refused = outcomes.filter((o) => o.result === 'refused');
    const notFound = outcomes.filter((o) => o.result === 'not_found').length;

    if (failed) {
      toast.error(
        done.length === 0
          ? t('products.archive.saveError')
          : archive
            ? t('products.archive.partialError', { count: done.length })
            : t('products.unarchive.partialError', { count: done.length }),
        { duration: Infinity },
      );
    } else if (done.length > 0) {
      toast.success(
        archive
          ? t('products.archive.done', { count: done.length })
          : t('products.unarchive.done', { count: done.length }),
      );
    }
    if (refused.length > 0) {
      const skuOf = (o: Outcome) => o.sku ?? session.targets.find((target) => target.id === o.skuId)?.sku ?? o.skuId;
      const named = refused.slice(0, REFUSED_NAMED_MAX).map(skuOf);
      const more = refused.length - REFUSED_NAMED_MAX;
      const andMore = more > 0 ? t('products.archive.andMore', { count: more }) : null;
      toast.warning(t('products.archive.refused', { count: refused.length }), {
        description: [named.join(', '), andMore].filter(Boolean).join(' '),
        duration: Infinity,
      });
    }
    if (notFound > 0) {
      toast.warning(t('products.archive.notFound', { count: notFound }), { duration: Infinity });
    }

    if (failed) {
      // Stay open on what is left: drop the SKUs already done and re-check
      // the rest. The re-check keeps confirm disabled, so it is never
      // re-enabled while anything is in flight.
      void utils.inventory.invalidate();
      const doneIds = new Set(done.map((o) => o.skuId));
      const remaining = session.targets.filter((target) => !doneIds.has(target.id));
      setSession({ mode: session.mode, targets: remaining });
      if (archive) void loadUsage(remaining.map((target) => target.id));
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onDone?.(refused.map((o) => o.skuId));
    onOpenChange(false);
    void utils.inventory.invalidate();
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submitting) return;
        onOpenChange(next);
      }}
    >
      <AlertDialogContent
        className="sm:max-w-xl"
        onEscapeKeyDown={(e) => {
          if (submitting) e.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {archive
              ? t('products.archive.title', { count: session.targets.length })
              : t('products.unarchive.title', { count: session.targets.length })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {archive ? t('products.archive.body') : t('products.unarchive.body')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {archive && (
          // -mx-px px-px pt-px: room for the amber callout's ring, which
          // overflow-y-auto would otherwise clip.
          <div className="-mx-px max-h-[55vh] space-y-4 overflow-y-auto px-px pt-px">
            {loading && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('products.archive.loading')}
              </p>
            )}

            {loadError && !loading && (
              <SectionError
                variant="compact"
                body={t('products.archive.loadError')}
                onRetry={() => void loadUsage(session.targets.map((target) => target.id))}
                retryLabel={t('products.archive.retry')}
              />
            )}

            {usage !== null && !loading && (
              <>
                {liveOnShopify > 0 && (
                  <div
                    className="flex gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-300/80 dark:bg-amber-950/30 dark:text-amber-200"
                    role="note"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <p>{t('products.archive.shopifyLiveWarning', { count: liveOnShopify })}</p>
                  </div>
                )}

                <p className="text-sm font-medium">
                  {t('products.archive.canArchive', { count: clear.length })}
                </p>

                {blocked.length > 0 && (
                  <SkuCodeList
                    heading={t('products.archive.blockedHeading', { count: blocked.length })}
                    items={blocked.map((r) => ({
                      id: r.target.id,
                      sku: r.target.sku,
                      lines: r.reasons.map(reasonText),
                    }))}
                  />
                )}

                {warned.length > 0 && (
                  <SkuCodeList heading={t('products.archive.warningsHeading')} items={warned} />
                )}

                {clear.length > 0 && (
                  <div className="space-y-1.5">
                    <label htmlFor="archive-skus-reason" className="text-sm font-medium">
                      {t('products.archive.reasonLabel')}
                      <span className="ml-1 text-destructive" aria-hidden="true">*</span>
                    </label>
                    <Textarea
                      id="archive-skus-reason"
                      value={reason}
                      maxLength={REASON_MAX}
                      placeholder={t('products.archive.reasonPlaceholder')}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={submitting}
                      aria-required="true"
                      aria-describedby={trimmedReason.length === 0 ? 'archive-skus-reason-hint' : undefined}
                      className="min-h-[64px]"
                    />
                    {trimmedReason.length === 0 && (
                      <p id="archive-skus-reason-hint" className="text-xs text-muted-foreground">
                        {t('products.archive.reasonRequired')}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>{t('common.cancel')}</AlertDialogCancel>
          {/* Archive is a reversible removal: orange outline (DESIGN §3.4,
              §13.6), never destructive red. Unarchive restores: primary. */}
          <Button
            type="button"
            variant={archive ? 'outline' : 'default'}
            className={
              archive
                ? 'border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700'
                : undefined
            }
            onClick={() => void runPass()}
            disabled={archive ? archiveDisabled : submitting || session.targets.length === 0}
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {submitting
              ? t('products.archive.archiving')
              : archive
                ? t('products.archive.confirm', { count: confirmCount })
                : t('products.unarchive.confirm', { count: confirmCount })}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
